import { useState, useEffect } from 'react'
import { Square } from 'lucide-react'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import { formatAudioTime, useAudioPlaybackTime } from '@/hooks/useAudioPlaybackTime'
import RecordingWaves from './RecordingWaves'

type RecorderState = 'idle' | 'recording' | 'preview' | 'submitted'

interface Props {
  label: string
  onSubmit: (blob: Blob) => Promise<void>
  existingUrl?: string | null
  startLabel?: string
  unavailableActionLabel?: string
  onUnavailableAction?: () => void
  submittedLabel?: string
  submittedHint?: string
  disabled?: boolean
  /** When set, recording auto-stops at this many seconds. */
  maxDurationSec?: number
}

export default function AudioRecorder({
  label,
  onSubmit,
  existingUrl,
  startLabel,
  unavailableActionLabel,
  onUnavailableAction,
  submittedLabel,
  submittedHint,
  disabled,
  maxDurationSec,
}: Props) {
  const recorder = useAudioRecorder()
  const [state, setState] = useState<RecorderState>(existingUrl ? 'submitted' : 'idle')
  const [submitting, setSubmitting] = useState(false)
  const playUrl = recorder.audioUrl ?? existingUrl ?? undefined
  const playback = useAudioPlaybackTime(playUrl, recorder.elapsedSec)

  useEffect(() => {
    if (
      maxDurationSec != null &&
      state === 'recording' &&
      recorder.elapsedSec >= maxDurationSec
    ) {
      recorder.stopRecording()
      setState('preview')
    }
  }, [maxDurationSec, state, recorder])

  async function handleStartRecording() {
    const started = await recorder.startRecording()
    if (started === false) return
    setState('recording')
  }

  function handleStopRecording() {
    recorder.stopRecording()
    setState('preview')
  }

  function handleReRecord() {
    recorder.resetRecording()
    setState('idle')
  }

  async function handleSubmit() {
    if (!recorder.audioBlob) return
    setSubmitting(true)
    try {
      await onSubmit(recorder.audioBlob)
      setState('submitted')
    } catch {
      // error handled by parent
    } finally {
      setSubmitting(false)
    }
  }

  if (state === 'submitted') {
    return (
      <div className="flex flex-col gap-3">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="text-lg text-green-600">✅</span>
            <span className="text-sm font-medium text-green-800">
              {submittedLabel ?? `${label} 제출됨`}
            </span>
            {playUrl ? (
              <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
                <audio
                  ref={playback.audioRef}
                  src={playUrl}
                  controls
                  className="h-8 min-w-0 max-w-full"
                  onLoadedMetadata={playback.handleLoadedMetadata}
                  onTimeUpdate={playback.handleTimeUpdate}
                  onPlay={playback.handlePlay}
                  onPause={playback.handlePause}
                  onEnded={playback.handleEnded}
                />
                <span className="shrink-0 text-xs tabular-nums text-green-700">
                  {formatAudioTime(playback.currentSec)} / {formatAudioTime(playback.durationSec)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        {submittedHint ? (
          <div className="rounded-xl border border-lc-lightblue-300 bg-lc-lightblue-100 p-4">
            <p className="text-[15px] font-medium leading-[1.5] tracking-[-0.3px] text-lc-gray-800">
              {submittedHint}
            </p>
          </div>
        ) : null}
      </div>
    )
  }

  if (state === 'preview' && recorder.audioUrl) {
    return (
      <div className="flex flex-col gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <audio
            ref={playback.audioRef}
            src={recorder.audioUrl}
            controls
            className="h-10 min-w-[180px] flex-1"
            onLoadedMetadata={playback.handleLoadedMetadata}
            onTimeUpdate={playback.handleTimeUpdate}
            onPlay={playback.handlePlay}
            onPause={playback.handlePause}
            onEnded={playback.handleEnded}
          />
          <span className="shrink-0 text-xs tabular-nums text-gray-500">
            {formatAudioTime(playback.currentSec)} / {formatAudioTime(playback.durationSec)}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReRecord}
            disabled={submitting}
            className="flex-1 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            다시 녹음
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? '제출 중...' : '제출'}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'recording') {
    return (
      <div className="flex flex-col items-center gap-3 p-6 bg-red-50 border border-red-200 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="size-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-red-700">녹음 중</span>
          <span className="font-mono text-sm text-red-600">
            {formatAudioTime(recorder.elapsedSec)}
          </span>
        </div>
        <div className="relative flex size-20 shrink-0 items-center justify-center">
          <RecordingWaves />
          <button
            type="button"
            onClick={handleStopRecording}
            aria-label="녹음 종료"
            className="relative z-10 flex size-20 items-center justify-center rounded-full bg-lc-error-500 text-white shadow-[0_6px_8px_0_rgb(255_103_94/0.4)] transition-colors hover:bg-lc-error-600 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lc-error-500"
          >
            <Square className="size-[22px] fill-current stroke-none" />
          </button>
        </div>
      </div>
    )
  }

  // idle
  return (
    <div className="flex flex-col items-center gap-2 p-4">
      {recorder.error && (
        <p className="text-xs text-red-500 mb-1">{recorder.error}</p>
      )}
      {recorder.unavailableReason && unavailableActionLabel && onUnavailableAction ? (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleStartRecording}
            disabled={disabled}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50 sm:max-w-60"
          >
            🎙️ {startLabel ?? '녹음 시작'}
          </button>
          <button
            type="button"
            onClick={onUnavailableAction}
            disabled={disabled}
            className="min-h-12 flex-1 rounded-xl border border-lc-gray-300 bg-white px-6 py-3 text-sm font-semibold text-lc-gray-700 transition-colors hover:bg-lc-gray-100 disabled:opacity-50 sm:max-w-60"
          >
            {unavailableActionLabel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleStartRecording}
          disabled={disabled}
          className="flex items-center gap-2 rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          🎙️ {startLabel ?? '녹음 시작'}
        </button>
      )}
    </div>
  )
}
